import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm, UseFormReturn } from "react-hook-form";

import { IUsePlanForm } from "./plan-form.hooks.interface";
import { planFormSchema, TPlanFormSchema } from "./plan-form.schema";
import { TPlanFormData } from "./plan-form.types";

export const usePlanForm = (
  initialData?: TPlanFormData,
  editingPlanoIndex?: number,
  projectFather?: { id: string; name: string },
  projectParticipantOrganizations?: Array<{ id: string; name: string }>
): IUsePlanForm => {
  // Debug log
  React.useEffect(() => {
    if (editingPlanoIndex !== undefined && initialData) {
      console.log('🔍 [usePlanForm] Hook initialized with:', {
        hasInitialData: !!initialData,
        hasInstallments: !!initialData?.installments,
        hasOtherInstallments: !!initialData?.otherInstallments,
        installmentsLength: initialData?.installments?.length || 0,
        installments: initialData?.installments,
        otherInstallments: initialData?.otherInstallments,
        projectFather,
      });
    }
  }, []);

  const [tempPlano, setTempPlano] = React.useState<TPlanFormData>({
    project_type: initialData?.project_type || "affinity",
    name: initialData?.name || "",
    description: initialData?.description || "",
    plan_features: initialData?.plan_features || [],
    markup: initialData?.markup || 0,
    markup_mode: initialData?.markup_mode || "percentage",
    products: initialData?.products || [],
    installments: initialData?.installments || [],
    otherInstallments: initialData?.otherInstallments || (projectFather && !initialData ? {
      mainPartner: {
        id: projectFather.id,
        name: projectFather.name,
        commission: 0,
        markup: 0,
      },
      participatingPartners: (projectParticipantOrganizations || []).map((org) => ({
        id: org.id,
        name: org.name,
        commission: 0,
        markup: 0,
      })),
    } : undefined),
    // Estrutura antiga (mantida para compatibilidade)
    commission_distribution:
      initialData?.commission_distribution || [
        { agency: 0, commission: 0, position: 0 },
      ],
    commission_lifetime_other_installments:
      initialData?.commission_lifetime_other_installments || 0,
  });

  const [validationErrors, setValidationErrors] = React.useState<{
    planFeatures?: string;
  }>({});

  const form = useForm<TPlanFormSchema>({
    resolver: zodResolver(planFormSchema) as any,
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      plan_features: initialData?.plan_features || [],
      markup: initialData?.markup || 0,
      markup_mode: initialData?.markup_mode || "percentage",
      products: initialData?.products || [],
      commission_distribution:
        initialData?.commission_distribution || [
          { agency: 0, commission: 0, position: 0 },
        ],
      commission_lifetime_other_installments:
        initialData?.commission_lifetime_other_installments || 0,
    },
  }) as UseFormReturn<TPlanFormSchema>;

  const isEditing = editingPlanoIndex !== undefined;

  // Cálculos de markup centralizado
  const totalMarkupPercentage =
    tempPlano.markup_mode === "percentage" ? tempPlano.markup || 0 : 0;
  const totalMarkupReal =
    tempPlano.markup_mode === "real_currency" ? tempPlano.markup || 0 : 0;

  // Função para validar o formulário
  const validateForm = React.useCallback(() => {
    const errors: { planFeatures?: string } = {};

    // Validar características do plano
    const validFeatures = tempPlano.plan_features.filter(
      (feature) => feature.trim() !== ""
    );

    if (validFeatures.length === 0) {
      errors.planFeatures = "Pelo menos uma característica é obrigatória";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [tempPlano.plan_features]);

  const handleSave = React.useCallback(() => {
    if (!validateForm()) {
      return; // Não salva se houver erros de validação
    }
    // A página irá lidar com o salvamento
  }, [validateForm]);

  const handleCancel = React.useCallback(() => {
    setValidationErrors({}); // Limpa erros ao cancelar
    // A página irá lidar com o cancelamento
  }, []);

  return {
    form,
    tempPlano,
    setTempPlano,
    validationErrors,
    setValidationErrors,
    validateForm,
    handleSave,
    handleCancel,
    isEditing,
    totalMarkupPercentage,
    totalMarkupReal,
  };
};

